<template>
  <Slideover :visible="visible">
    <div>
      <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
        <div class="flex items-start justify-between space-x-3">
          <h2 class="text-lg leading-7 font-medium">
            {{ title }}
          </h2>
          <div class="h-7 flex items-center">
            <button
              type="button"
              aria-label="Close panel"
              @click="$emit('toggle-slideover', 'customerEdit')"
              class="text-green-300 hover:text-white transition ease-in-out duration-150"
            >
              <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <!-- Main -->
      <div class="relative py-3 px-2 mx-4 space-y-4">
        <!-- Sales Rep -->
        <div>
          <label
            for="sales_rep"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Sales Rep
          </label>
          <div class="mt-1 rounded-md shadow-sm">
            <input
              type="text"
              class="form-input block w-full transition duration-150 ease-in-out text-sm leading-5 disabled:text-gray-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
              id="sales_rep"
              list="sales_reps"
              autocomplete="off"
              v-model="sales_rep"
              :disabled="! userCanEditSalesRep"
            />
            <datalist id="sales_reps">
              <option v-for="(rep, index) in salesReps" :key="index">{{
                rep
              }}</option>
            </datalist>
          </div>
        </div>

        <!-- Check boxes -->
        <div class="space-y-2">
          <div class="flex items-center space-x-2">
            <input
              type="checkbox"
              id="zone_release"
              class="form-checkbox h-4 w-4 text-green-600"
              v-model="zone_release"
            />
            <label for="zone_release">
              Zone Release
            </label>
          </div>
          <div class="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_this_a_gift"
              class="form-checkbox h-4 w-4 text-green-600"
              v-model="is_this_a_gift"
            />
            <label for="is_this_a_gift">
              Is This a Gift?
            </label>
          </div>
          <div class="flex items-center space-x-2">
            <input
              type="checkbox"
              id="trustpilot_email_sent"
              class="form-checkbox h-4 w-4 text-green-600"
              v-model="trustpilot_email_sent"
            />
            <label for="trustpilot_email_sent">
              TrustPilot Email Sent
            </label>
          </div>
          <div class="flex items-center space-x-2">
            <input
              type="checkbox"
              id="block_future_shipping"
              class="form-checkbox h-4 w-4 text-green-600"
              v-model="block_future_shipping"
            />
            <label for="block_future_shipping">
              Block Future Shipping
            </label>
          </div>
          <div class="flex items-center space-x-2">
            <input
              type="checkbox"
              id="ignore_fraud_score"
              class="form-checkbox h-4 w-4 text-green-600"
              v-model="ignore_fraud_score"
            />
            <label for="ignore_fraud_score">
              Ignore Fraud Score
            </label>
          </div>
          <div class="flex items-center space-x-2">
            <input
              type="checkbox"
              :id="`priority_${_uid}`"
              class="form-checkbox h-4 w-4 text-green-600"
              v-model="priority"
            />
            <label :for="`priority_${_uid}`">
              Priority
            </label>
          </div>
          <div class="flex items-center space-x-2">
            <input
              type="checkbox"
              id="do_not_advertise"
              class="form-checkbox h-4 w-4 text-green-600"
              v-model="do_not_advertise"
            />
            <label for="do_not_advertise">
              Do Not Advertise
            </label>
          </div>
        </div>

        <!-- Gift Message -->
        <div>
          <label
            for="gift_message"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Gift Message
          </label>
          <div class="mt-1 rounded-md shadow-sm">
            <textarea
              id="gift_message"
              class="form-input block w-full h-24 transition duration-150 ease-in-out text-sm leading-5"
              v-model="gift_message"
            ></textarea>
          </div>
        </div>
      </div>
      <!-- Footer -->
      <div class="flex-shrink-0 px-4 py-4 space-x-4 flex justify-end">
        <span class="inline-flex rounded-md shadow-sm">
          <button
            type="button"
            @click="$emit('toggle-slideover')"
            class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            Cancel
          </button>
        </span>
        <span class="inline-flex rounded-md shadow-sm">
          <button
            type="submit"
            class="inline-flex justify-center items-center space-x-2 py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green active:bg-green-700 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            @click="update()"
            :disabled="updating"
          >
            <span>Save</span>
            <LoadingSpinner v-if="updating" />
          </button>
        </span>
      </div>
    </div>
  </Slideover>
</template>
<script>
import auth from '@/api/auth';
import { ApiFactory } from "@/api/ApiFactory.js";
import Slideover from "@/components/layout/Slideover.vue";

const Listing = ApiFactory.get('listings');

export default {
  name: "OrderMiscEdit",
  components: {
    Slideover
  },
  props: {
    title: {
      type: String,
      default: "Title"
    },
    visible: {
      type: Boolean,
      default: false
    },
    order: {
      type: [Array, Object],
      required: true
    }
  },
  data() {
    return {
      sales_rep: this.order.RefName,
      zone_release: this.toBoolean(this.order.ZoneRelease),
      is_this_a_gift: this.toBoolean(this.order.LocalSortText2),
      trustpilot_email_sent: this.toBoolean(this.order.LocalSortInteger3),
      block_future_shipping: this.toBoolean(this.order.LocalSortInteger4),
      ignore_fraud_score: this.toBoolean(this.order.LocalSortInteger2),
      priority: this.toBoolean(this.order.Priority),
      do_not_advertise: this.toBoolean(this.order.DoNotAdvertise),
      gift_message: this.order.GiftMessage,
      salesReps: [],
      updating: false
    };
  },
  computed: {
    userCanEditSalesRep() {
      return auth.isUserAuthorized(['supervisor','third-party']);
    }
  },
  created() {
    Listing.get({ list: 'Customer Service Representatives' })
      .then(response => {
        this.salesReps = response.data.body.map((entry) => entry.Text1);
      });
  },
  methods: {
    toBoolean(value) {
      if (
        value === null ||
        value === false ||
        value === 0 ||
        value === "0" ||
        String(value).trim() === "" ||
        String(value).toLowerCase().trim() === "no" ||
        value === [] ||
        value === {}
      )
        return false;
      return true;
    },
    update() {
      this.updating = true;
      this.$store
        .dispatch("orders/updateOrder", {
          OrderNumber: this.order.SourceOrderNumber,
          data: {
            RefName: this.sales_rep,
            LocalSortInteger2: this.ignore_fraud_score ? 1 : 0,
            LocalSortInteger3: this.trustpilot_email_sent ? 1 : 0,
            LocalSortInteger4: this.block_future_shipping ? 1 : 0,
            LocalSortText2: this.is_this_a_gift ? 'Yes' : 'No',
            ZoneRelease: this.zone_release ? 1 : 0,
            DoNotAdvertise: this.do_not_advertise ? 1 : 0,
            Priority: this.priority ? 1 : 0,
            GiftMessage: this.gift_message
          }
        })
        .then(() => {
          this.updating = false;
          this.reloadOrder();
          this.$emit("toggle-slideover");
        });
    },
    reloadOrder() {
      this.$store.dispatch("orders/refreshOrder", this.order.SourceOrderNumber);
    },
  }
};
</script>
